import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { buildBlankTemplate } from "../lib/blankTemplate";
import { ApiError } from "../lib/api";
import { createTemplate, generateTemplate } from "../lib/templates";
import { Button } from "./ui/button";

/** Prompt starters — collapse "what do I write?" into one tap (conversion). */
const EXAMPLE_PROMPTS: ReadonlyArray<{ chip: string; prompt: string }> = [
  { chip: "Welcome email", prompt: "A warm welcome email for new subscribers introducing our brand." },
  { chip: "20% promo", prompt: "A promotional email announcing a 20% off sale with a clear call to action." },
  { chip: "Re-engagement", prompt: "A re-engagement email to win back subscribers who haven't opened in a while." },
];

/**
 * "Create a new email" — three visible paths, AI-first (§C.1). The AI card is
 * the dominant, accented path: a prompt + example chips that call the sync
 * blueprint generator (POST /v1/templates/generate) and drop the user straight
 * into the editor. "Browse templates" opens the gallery; "Start from scratch"
 * mints a blank document. The shortest route to a first email leads with AI.
 */
export function CreateEmailZone({ onBrowse }: { onBrowse: () => void }): JSX.Element {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");

  const generate = useMutation({
    mutationFn: () => generateTemplate(prompt.trim()),
    onSuccess: (tpl) => navigate(`/templates/${tpl.id}/edit`),
  });

  const blank = useMutation({
    mutationFn: () => createTemplate(buildBlankTemplate({ name: "Untitled template" })),
    onSuccess: (tpl) => navigate(`/templates/${tpl.id}/edit`),
  });

  const busy = generate.isPending || blank.isPending;
  const canGenerate = prompt.trim() !== "" && !busy;

  function errorOf(e: unknown): string {
    return e instanceof ApiError ? e.problem.detail || e.problem.title : "Something went wrong. Try again.";
  }

  return (
    <section aria-labelledby="create-email-heading" className="space-y-3">
      <h2 id="create-email-heading" className="text-sm font-semibold text-neutral-700">
        Create a new email
      </h2>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.6fr_1fr_1fr]">
        {/* ── AI (accent, primary path) ── */}
        <div className="relative flex flex-col gap-3 rounded-lg border-2 border-primary-300 bg-primary-50/50 p-4 shadow-sm">
          <span className="absolute right-3 top-3 rounded-full bg-primary-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
            Recommended
          </span>
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-neutral-900">Generate with AI</h3>
            <p className="text-xs text-neutral-600">Describe your email — we'll draft a complete, on-brand template.</p>
          </div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            maxLength={4000}
            placeholder="Describe your email…  e.g. a launch announcement for our new pricing"
            aria-label="Describe your email"
            disabled={busy}
            className="w-full resize-none rounded-md border border-neutral-300 px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 disabled:bg-neutral-50"
          />
          <div className="flex flex-wrap gap-1.5">
            {EXAMPLE_PROMPTS.map((ex) => (
              <button
                key={ex.chip}
                type="button"
                disabled={busy}
                onClick={() => setPrompt(ex.prompt)}
                className="rounded-full border border-primary-200 bg-white px-2.5 py-1 text-xs text-primary-700 transition-colors hover:bg-primary-100 disabled:opacity-50"
              >
                {ex.chip}
              </button>
            ))}
          </div>
          <Button
            variant="primary"
            loading={generate.isPending}
            disabled={!canGenerate}
            onClick={() => generate.mutate()}
          >
            {generate.isPending ? "Generating your email…" : "Generate"}
          </Button>
          {generate.isError && (
            <p role="alert" className="text-xs text-error-700">
              {errorOf(generate.error)}
            </p>
          )}
        </div>

        {/* ── Browse templates ── */}
        <button
          type="button"
          onClick={onBrowse}
          disabled={busy}
          className="flex flex-col items-start gap-1 rounded-lg border border-neutral-200 bg-white p-4 text-left shadow-sm transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50"
        >
          <h3 className="text-base font-semibold text-neutral-900">Browse templates</h3>
          <p className="text-xs text-neutral-600">Start from a ready-made design and make it yours.</p>
          <span className="mt-auto pt-2 text-sm font-medium text-primary-700">Open gallery →</span>
        </button>

        {/* ── Start from scratch ── */}
        <button
          type="button"
          onClick={() => blank.mutate()}
          disabled={busy}
          className="flex flex-col items-start gap-1 rounded-lg border border-neutral-200 bg-white p-4 text-left shadow-sm transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50"
        >
          <h3 className="text-base font-semibold text-neutral-900">Start from scratch</h3>
          <p className="text-xs text-neutral-600">Open a blank canvas and build block by block.</p>
          <span className="mt-auto pt-2 text-sm font-medium text-primary-700">
            {blank.isPending ? "Creating…" : "Blank canvas →"}
          </span>
        </button>
      </div>
      {blank.isError && (
        <p role="alert" className="text-xs text-error-700">
          {errorOf(blank.error)}
        </p>
      )}
    </section>
  );
}
